import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Edit, Plus, RefreshCw, Printer } from 'lucide-react';
import { useAuth } from '../../../../../../contexts/AuthContext';
import { stcBinService } from '../../../../../../services/stcBinService';
import { stcZoneService } from '../../../../../../services/stcZoneService';
import { stcBinTypeService } from '../../../../../../services/stcBinTypeService';
import { stcBinSizeService } from '../../../../../../services/stcBinSizeService';
import { stcBinGrpService } from '../../../../../../services/stcBinGrpService';
import { stcBinCategoryService } from '../../../../../../services/stcBinCategoryService';
import { stcResourceService } from '../../../../../../services/stcResourceService';
import { stcPriorityService } from '../../../../../../services/stcPriorityService';
import { stcMovementTypeService } from '../../../../../../services/stcMovementTypeService';
import { warehouseService } from '../../../../../../services/warehouseService';
import { StcBinPagedDto, StcBinsPagedQueryParams, UpdateStcBinRequest } from '../../../../../../types/stcBin';
import { EnrichedBin, MasterDataLookups } from './types';
import { BulkUpdateModal } from '../../../rack-builder/BulkUpdateModal';
import { LocationCreateModal } from './LocationCreateModal';
import { LabelPrintModal } from '../../../../../common/LabelPrintModal';
import { WarehouseTable, useInlineEdit } from '../../../../../shared/table';
import { createColumnFactory } from '../../../../../shared/table/utils/tableUtils';
import type { ColumnDefinition, TableAction, ServerFetchParams } from '../../../../../shared/table/types/table';

interface LocationsDataTableProps {
  warehouseID?: string;
}

export const LocationsDataTable: React.FC<LocationsDataTableProps> = ({ warehouseID }) => {
  const { user } = useAuth();
  const companyNo = user?.companyNo || '';

  // Server-side pagination state
  const [serverBins, setServerBins] = useState<EnrichedBin[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [masterData, setMasterData] = useState<MasterDataLookups | null>(null);

  // UI state
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedBinCodes, setSelectedBinCodes] = useState<string[]>([]);
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);

  // Guard against multiple simultaneous calls
  const isLoadingRef = useRef(false);
  // Track whether initial load is done (to avoid full-screen spinner on filter changes)
  const initialLoadDoneRef = useRef(false);
  // Store latest fetch params for refresh after edit/delete
  const latestFetchParamsRef = useRef<StcBinsPagedQueryParams>({ pageNumber: 1, pageSize: 50 });
  // Debounce timer for column filters
  const filterDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Master data ref for use in fetch callback
  const masterDataRef = useRef<MasterDataLookups | null>(null);

  // Create the column factory
  const columnFactory = createColumnFactory<EnrichedBin>();

  // Enrich a server-paged bin with client-side master data lookups
  const enrichBin = useCallback((bin: StcBinPagedDto, lookups: MasterDataLookups | null): EnrichedBin => ({
    ...bin,
    // 5 descriptions come from server LEFT JOINs:
    zoneName: bin.zoneDescription,
    binTypeName: bin.binTypeDescription,
    binSizeName: bin.binSizeDescription,
    binGrpName: bin.binGrpDescription,
    binCategoryName: bin.binCategoryDescription,
    // 5 descriptions resolved client-side from master data (not in server JOINs):
    resourceName: lookups && bin.resourceNo ? lookups.resources.get(bin.resourceNo)?.description1 : undefined,
    pickPriorityName: lookups && bin.pickPriorityNo ? lookups.priorities.get(bin.pickPriorityNo)?.description1 : undefined,
    putAwayPriorityName: lookups && bin.putAwayPriorityNo ? lookups.priorities.get(bin.putAwayPriorityNo)?.description1 : undefined,
    movementTypeName: lookups && bin.movementTypeNo ? lookups.movementTypes.get(bin.movementTypeNo)?.description1 : undefined,
    warehouseName: lookups && bin.warehouseID ? lookups.warehouses.get(bin.warehouseID)?.warehouseDescr : undefined,
  }), []);

  // Fetch paged bins from server
  const fetchPagedBins = useCallback(async (params: StcBinsPagedQueryParams) => {
    if (!companyNo) return;
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;

    // Only show full loading spinner on initial load — subsequent fetches keep table visible
    if (!initialLoadDoneRef.current) {
      setIsLoading(true);
    }
    setError(null);

    try {
      // If warehouseID prop is set, add it as a filter
      const queryParams = warehouseID
        ? { ...params, warehouseID }
        : params;

      const result = await stcBinService.getCurrentPaged(queryParams);

      const enriched: EnrichedBin[] = result.items.map((bin: StcBinPagedDto) =>
        enrichBin(bin, masterDataRef.current)
      );

      setServerBins(enriched);
      setTotalRecords(result.totalRecords);
      latestFetchParamsRef.current = params;
      initialLoadDoneRef.current = true;
    } catch (err) {
      console.error('Error loading paged bins:', err);
      setError('Kunne ikke laste lokasjonsdata. Prøv igjen.');
    } finally {
      setIsLoading(false);
      isLoadingRef.current = false;
    }
  }, [companyNo, warehouseID, enrichBin]);

  // Load master data (for dropdown edit options) — only once on mount
  const loadMasterData = useCallback(async () => {
    if (!companyNo) return;
    try {
      const [
        warehousesData,
        zonesData,
        binTypesData,
        binSizesData,
        binGroupsData,
        binCategoriesData,
        resourcesData,
        prioritiesData,
        movementTypesData
      ] = await Promise.all([
        warehouseService.getWarehouses(),
        stcZoneService.getCurrent(),
        stcBinTypeService.getCurrent(),
        stcBinSizeService.getCurrent(),
        stcBinGrpService.getCurrent(),
        stcBinCategoryService.getCurrent(),
        stcResourceService.getCurrent(),
        stcPriorityService.getCurrent(),
        stcMovementTypeService.getCurrent()
      ]);

      const lookups: MasterDataLookups = {
        warehouses: new Map(warehousesData.map(w => [w.warehouseID, w])),
        zones: new Map(zonesData.map(z => [z.stcZoneID, z])),
        binTypes: new Map(binTypesData.map(t => [t.binTypeNo, t])),
        binSizes: new Map(binSizesData.map(s => [s.binSizeNo, s])),
        binGroups: new Map(binGroupsData.map(g => [g.binGrpNo, g])),
        binCategories: new Map(binCategoriesData.map(c => [c.stcBinCatNo, c])),
        resources: new Map(resourcesData.map(r => [r.resourceNo, r])),
        priorities: new Map(prioritiesData.map(p => [p.priorityNo, p])),
        movementTypes: new Map(movementTypesData.map(m => [m.movementTypeNo, m]))
      };

      setMasterData(lookups);
      masterDataRef.current = lookups;
    } catch (err) {
      console.error('Error loading master data for dropdowns:', err);
    }
  }, [companyNo]);

  // Server-side pagination callback — called by WarehouseTable when page/sort/filter changes
  const handleServerFetch = useCallback((params: ServerFetchParams) => {
    const f = params.filters;
    const queryParams: StcBinsPagedQueryParams = {
      pageNumber: params.pageNumber,
      pageSize: params.pageSize,
      sortColumn: params.sortColumn,
      sortDirection: params.sortDirection,
      binCode: f?.binCode || undefined,
      description1: f?.description1 || undefined,
      warehouseID: f?.warehouseID || undefined,
      stcZoneID: f?.stcZoneID || undefined,
      binGrpNo: f?.binGrpNo || undefined,
      stcBinCatNo: f?.stcBinCatNo || undefined,
      binTypeNo: f?.binTypeNo || undefined,
      binSizeNo: f?.binSizeNo || undefined,
      movementTypeNo: f?.movementTypeNo || undefined,
      resourceNo: f?.resourceNo || undefined,
      pickPriorityNo: f?.pickPriorityNo || undefined,
      putAwayPriorityNo: f?.putAwayPriorityNo || undefined,
      customSortID: f?.customSortID || undefined,
      replenishMinLevel: f?.replenishMinLevel || undefined,
      replenishMaxLevel: f?.replenishMaxLevel || undefined,
      sharedBinMaxQuantity: f?.sharedBinMaxQuantity || undefined,
      pickCustomOrder: f?.pickCustomOrder || undefined,
      putAwayCustomOrder: f?.putAwayCustomOrder || undefined,
      itemNo: f?.itemNo || undefined,
      pickableYesNo: f?.pickableYesNo || undefined,
      reservationAllowed: f?.reservationAllowed || undefined,
      autoReplenishment: f?.autoReplenishment || undefined,
      negativeReservationAllowed: f?.negativeReservationAllowed || undefined,
      sharedBinYesNo: f?.sharedBinYesNo || undefined,
      sharedBinPreferEmptyBin: f?.sharedBinPreferEmptyBin || undefined,
      lastCountedDate: f?.lastCountedDate || undefined,
      lastMovementDate: f?.lastMovementDate || undefined,
    };

    // Debounce filter changes (300ms) but execute page/sort changes immediately
    if (filterDebounceRef.current) {
      clearTimeout(filterDebounceRef.current);
    }

    // Detect if any filter value changed (compared to previous fetch)
    const prevFilters = latestFetchParamsRef.current;
    const filterKeys: (keyof StcBinsPagedQueryParams)[] = [
      'binCode', 'description1', 'warehouseID', 'stcZoneID',
      'binGrpNo', 'stcBinCatNo', 'binTypeNo', 'binSizeNo',
      'movementTypeNo', 'resourceNo', 'pickPriorityNo', 'putAwayPriorityNo',
      'customSortID', 'replenishMinLevel', 'replenishMaxLevel', 'sharedBinMaxQuantity',
      'pickCustomOrder', 'putAwayCustomOrder', 'itemNo',
      'pickableYesNo', 'reservationAllowed', 'autoReplenishment',
      'negativeReservationAllowed', 'sharedBinYesNo', 'sharedBinPreferEmptyBin',
      'lastCountedDate', 'lastMovementDate',
    ];
    const isFilterChange = params.pageNumber === 1 &&
      filterKeys.some(key => queryParams[key] !== prevFilters[key]);

    if (isFilterChange) {
      filterDebounceRef.current = setTimeout(() => {
        fetchPagedBins(queryParams);
      }, 300);
    } else {
      fetchPagedBins(queryParams);
    }
  }, [fetchPagedBins]);

  // Refresh helper — re-fetches with the latest params
  const refreshData = useCallback(() => {
    fetchPagedBins(latestFetchParamsRef.current);
  }, [fetchPagedBins]);

  // Initial load
  useEffect(() => {
    fetchPagedBins({ pageNumber: 1, pageSize: 50 });
    loadMasterData();
  }, [fetchPagedBins, loadMasterData]);

  // Cleanup debounce timer
  useEffect(() => {
    return () => {
      if (filterDebounceRef.current) {
        clearTimeout(filterDebounceRef.current);
      }
    };
  }, []);

  // Inline editing
  const inlineEditConfig = useInlineEdit<EnrichedBin>({
    getRowId: (row) => row.binCode,
    onSave: async (_originalRow, editedData) => {
      const request: UpdateStcBinRequest = {
        warehouseID: editedData.warehouseID as string | undefined,
        stcZoneID: editedData.stcZoneID as string | undefined,
        binCode: editedData.binCode as string | undefined,
        description1: editedData.description1,
        binSizeNo: editedData.binSizeNo as number,
        binTypeNo: editedData.binTypeNo as number,
        resourceNo: editedData.resourceNo as number | undefined,
        itemNo: editedData.itemNo as string | undefined,
        binGrpNo: editedData.binGrpNo as number | undefined,
        pickPriorityNo: editedData.pickPriorityNo as number | undefined,
        pickCustomOrder: editedData.pickCustomOrder as string | undefined,
        replenishMinLevel: editedData.replenishMinLevel as number | undefined,
        replenishMaxLevel: editedData.replenishMaxLevel as number | undefined,
        movementTypeNo: editedData.movementTypeNo as number | undefined,
        putAwayCustomOrder: editedData.putAwayCustomOrder as string | undefined,
        stcBinCatNo: editedData.stcBinCatNo as number | undefined,
        customSortID: editedData.customSortID as number | undefined,
        sharedBinYesNo: editedData.sharedBinYesNo as boolean | undefined,
        sharedBinMaxQuantity: editedData.sharedBinMaxQuantity as number | undefined,
        putAwayPriorityNo: editedData.putAwayPriorityNo as number | undefined,
        reservationAllowed: editedData.reservationAllowed as boolean | undefined,
        negativeReservationAllowed: editedData.negativeReservationAllowed as boolean | undefined,
        autoReplenishment: editedData.autoReplenishment as boolean | undefined,
        pickableYesNo: editedData.pickableYesNo as boolean | undefined,
        sharedBinPreferEmptyBin: editedData.sharedBinPreferEmptyBin as boolean | undefined
      };
      await stcBinService.update(
        companyNo,
        _originalRow.warehouseID,
        _originalRow.binCode,
        request
      );
    },
    onSaveSuccess: () => refreshData(),
    onSaveError: (err) => setError(err.message || 'Feil ved lagring')
  });

  // Build dropdown options from master data for inline editing
  const zoneOptions = useMemo(() =>
    masterData ? Array.from(masterData.zones.values()).map(z => ({
      value: z.stcZoneID, label: `${z.stcZoneID} - ${z.description1}`
    })) : [], [masterData]);

  const binGrpOptions = useMemo(() =>
    masterData ? Array.from(masterData.binGroups.values()).map(g => ({
      value: g.binGrpNo, label: `${g.binGrpNo} - ${g.description1}`
    })) : [], [masterData]);

  const binTypeOptions = useMemo(() =>
    masterData ? Array.from(masterData.binTypes.values()).map(t => ({
      value: t.binTypeNo, label: `${t.binTypeNo} - ${t.description1}`
    })) : [], [masterData]);

  const binSizeOptions = useMemo(() =>
    masterData ? Array.from(masterData.binSizes.values()).map(s => ({
      value: s.binSizeNo, label: `${s.binSizeNo} - ${s.description1}`
    })) : [], [masterData]);

  const binCategoryOptions = useMemo(() =>
    masterData ? Array.from(masterData.binCategories.values()).map(c => ({
      value: c.stcBinCatNo, label: `${c.stcBinCatNo} - ${c.lng_1}`
    })) : [], [masterData]);

  const resourceOptions = useMemo(() =>
    masterData ? Array.from(masterData.resources.values()).map(r => ({
      value: r.resourceNo, label: `${r.resourceNo} - ${r.description1}`
    })) : [], [masterData]);

  const priorityOptions = useMemo(() =>
    masterData ? Array.from(masterData.priorities.values()).map(p => ({
      value: p.priorityNo, label: `${p.priorityNo} - ${p.description1}`
    })) : [], [masterData]);

  const movementTypeOptions = useMemo(() =>
    masterData ? Array.from(masterData.movementTypes.values()).map(m => ({
      value: m.movementTypeNo, label: `${m.movementTypeNo} - ${m.description1}`
    })) : [], [masterData]);

  const warehouseOptions = useMemo(() =>
    masterData ? Array.from(masterData.warehouses.values()).map(w => ({
      value: w.warehouseID, label: `${w.warehouseID} - ${w.warehouseDescr}`
    })) : [], [masterData]);

  // Handle bulk edit button
  const handleBulkEdit = useCallback(() => {
    if (selectedBinCodes.length === 0) {
      alert('Velg minst én lokasjon for å redigere');
      return;
    }
    setShowBulkEditModal(true);
  }, [selectedBinCodes.length]);

  // Handle create new location
  const handleCreateNew = useCallback(() => {
    setShowCreateModal(true);
  }, []);

  // Handle modal update success
  const handleUpdateSuccess = async () => {
    refreshData();
    setSelectedBinCodes([]);
    setShowBulkEditModal(false);
  };

  // Handle modal delete success
  const handleDeleteSuccess = async () => {
    refreshData();
    setSelectedBinCodes([]);
    setShowBulkEditModal(false);
  };

  // Handle create success
  const handleCreateSuccess = async () => {
    refreshData();
    setShowCreateModal(false);
  };

  // Column definitions using the factory
  const columns: ColumnDefinition<EnrichedBin>[] = useMemo(() => [
    columnFactory.createTextColumn('warehouseID', 'warehouseID', 'Lagersted', {
      minWidth: 150,
      sortable: true,
      filterType: 'both',
      formatter: (value: any, item: EnrichedBin) => item.warehouseName ? `${value} - ${item.warehouseName}` : String(value || '-'),
      editable: true,
      editType: 'select',
      editOptions: warehouseOptions
    }),

    columnFactory.createTextColumn('stcZoneID', 'stcZoneID', 'Sone', {
      minWidth: 150,
      sortable: true,
      filterType: 'both',
      formatter: (value: any, item: EnrichedBin) => item.zoneName ? `${value} - ${item.zoneName}` : String(value || '-'),
      editable: true,
      editType: 'select',
      editOptions: zoneOptions
    }),

    columnFactory.createTextColumn('binCode', 'binCode', 'Lokasjon', {
      minWidth: 120,
      sortable: true,
      filterType: 'both',
      editable: true
    }),

    columnFactory.createTextColumn('description1', 'description1', 'Beskrivelse', {
      minWidth: 200,
      sortable: true,
      filterType: 'both',
      editable: true
    }),

    columnFactory.createNumberColumn('binGrpNo', 'binGrpNo', 'Lok.Grp', {
      minWidth: 150,
      sortable: true,
      filterType: 'both',
      formatter: (value: any, item: EnrichedBin) => item.binGrpName || String(value || '-'),
      editable: true,
      editType: 'select',
      editOptions: binGrpOptions
    }),

    columnFactory.createNumberColumn('binTypeNo', 'binTypeNo', 'Type', {
      minWidth: 150,
      sortable: true,
      filterType: 'both',
      formatter: (value: any, item: EnrichedBin) => item.binTypeName || String(value || '-'),
      editable: true,
      editType: 'select',
      editOptions: binTypeOptions
    }),

    columnFactory.createNumberColumn('binSizeNo', 'binSizeNo', 'Størrelse', {
      minWidth: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value: any, item: EnrichedBin) => item.binSizeName || String(value || '-'),
      editable: true,
      editType: 'select',
      editOptions: binSizeOptions
    }),

    columnFactory.createNumberColumn('stcBinCatNo', 'stcBinCatNo', 'Lok.Kategori', {
      minWidth: 150,
      sortable: true,
      filterType: 'both',
      formatter: (value: any, item: EnrichedBin) => item.binCategoryName || String(value || '-'),
      editable: true,
      editType: 'select',
      editOptions: binCategoryOptions
    }),

    columnFactory.createNumberColumn('movementTypeNo', 'movementTypeNo', 'Bevegelseskode', {
      minWidth: 160,
      sortable: true,
      filterType: 'both',
      formatter: (value: any, item: EnrichedBin) => item.movementTypeName ? `${value} - ${item.movementTypeName}` : String(value || '-'),
      editable: true,
      editType: 'select',
      editOptions: movementTypeOptions
    }),

    columnFactory.createNumberColumn('resourceNo', 'resourceNo', 'Ressurs', {
      minWidth: 150,
      sortable: true,
      filterType: 'both',
      formatter: (value: any, item: EnrichedBin) => item.resourceName || String(value || '-'),
      editable: true,
      editType: 'select',
      editOptions: resourceOptions
    }),

    columnFactory.createBooleanColumn('reservationAllowed', 'reservationAllowed', 'Reserverbar', {
      minWidth: 110,
      sortable: true,
      filterType: 'both',
      editable: true
    }),

    columnFactory.createBooleanColumn('autoReplenishment', 'autoReplenishment', 'Automatisk påfylling', {
      minWidth: 160,
      sortable: true,
      filterType: 'both',
      editable: true
    }),

    columnFactory.createBooleanColumn('pickableYesNo', 'pickableYesNo', 'Plukklokasjon', {
      minWidth: 130,
      sortable: true,
      filterType: 'both',
      editable: true
    }),

    columnFactory.createBooleanColumn('negativeReservationAllowed', 'negativeReservationAllowed', 'Negativ beholdning gyldig', {
      minWidth: 190,
      sortable: true,
      filterType: 'both',
      editable: true
    }),

    columnFactory.createDateColumn('lastCountedDate', 'lastCountedDate', 'Siste telledato', {
      minWidth: 140,
      sortable: true,
      filterType: 'both'
    }),

    columnFactory.createDateColumn('lastMovementDate', 'lastMovementDate', 'Siste transaksjonsdato', {
      minWidth: 180,
      sortable: true,
      filterType: 'both'
    }),

    columnFactory.createNumberColumn('pickPriorityNo', 'pickPriorityNo', 'Prioritet, plukk', {
      minWidth: 140,
      sortable: true,
      filterType: 'both',
      formatter: (value: any, item: EnrichedBin) => item.pickPriorityName || String(value || '-'),
      editable: true,
      editType: 'select',
      editOptions: priorityOptions
    }),

    columnFactory.createNumberColumn('putAwayPriorityNo', 'putAwayPriorityNo', 'Prioritet, utlegg', {
      minWidth: 150,
      sortable: true,
      filterType: 'both',
      formatter: (value: any, item: EnrichedBin) => item.putAwayPriorityName || String(value || '-'),
      editable: true,
      editType: 'select',
      editOptions: priorityOptions
    }),

    columnFactory.createNumberColumn('customSortID', 'customSortID', 'Sortering', {
      minWidth: 100,
      sortable: true,
      filterType: 'both',
      editable: true
    }),

    columnFactory.createTextColumn('pickCustomOrder', 'pickCustomOrder', 'Plukkrekkefølge', {
      minWidth: 140,
      sortable: true,
      filterType: 'both',
      editable: true
    }),

    columnFactory.createTextColumn('putAwayCustomOrder', 'putAwayCustomOrder', 'Innlagringsrekkefølge', {
      minWidth: 160,
      sortable: true,
      filterType: 'both',
      editable: true
    }),

    columnFactory.createNumberColumn('replenishMinLevel', 'replenishMinLevel', 'Påfylling min %', {
      minWidth: 130,
      sortable: true,
      filterType: 'both',
      editable: true
    }),

    columnFactory.createNumberColumn('replenishMaxLevel', 'replenishMaxLevel', 'Påfylling maks %', {
      minWidth: 140,
      sortable: true,
      filterType: 'both',
      editable: true
    }),

    columnFactory.createTextColumn('itemNo', 'itemNo', 'Varenr.', {
      minWidth: 120,
      sortable: true,
      filterType: 'both',
      editable: true
    }),

    columnFactory.createBooleanColumn('sharedBinYesNo', 'sharedBinYesNo', 'FellesLokasjon', {
      minWidth: 130,
      sortable: true,
      filterType: 'both',
      editable: true
    }),

    columnFactory.createNumberColumn('sharedBinMaxQuantity', 'sharedBinMaxQuantity', 'Maks ant. på felleslok.', {
      minWidth: 180,
      sortable: true,
      filterType: 'both',
      editable: true
    }),

    columnFactory.createBooleanColumn('sharedBinPreferEmptyBin', 'sharedBinPreferEmptyBin', 'Prioriter tomme felleslok.', {
      minWidth: 200,
      sortable: true,
      filterType: 'both',
      editable: true
    })
  ], [binGrpOptions, binTypeOptions, binSizeOptions, binCategoryOptions, resourceOptions, priorityOptions, movementTypeOptions, warehouseOptions, zoneOptions]);

  // Custom row ID function
  const getRowId = useCallback((item: EnrichedBin) => item.binCode, []);

  // Handle selection change
  const handleSelectionChange = useCallback((selectedIds: (string | number)[], selectedData: EnrichedBin[]) => {
    setSelectedBinCodes(selectedIds.map(id => String(id)));
  }, []);

  // Table actions for bulk operations
  const tableActions: TableAction<EnrichedBin>[] = useMemo(() => [
    {
      id: 'create',
      label: 'Ny lokasjon',
      icon: Plus,
      onClick: () => handleCreateNew(),
      variant: 'primary'
    },
    {
      id: 'print-labels',
      label: `Skriv ut etiketter (${selectedBinCodes.length})`,
      icon: Printer,
      onClick: () => setShowPrintModal(true),
      disabled: (_data: EnrichedBin[], _selected?: EnrichedBin[]) => selectedBinCodes.length === 0,
      variant: 'secondary'
    },
    {
      id: 'bulk-edit',
      label: `Vedlikehold (${selectedBinCodes.length})`,
      icon: Edit,
      onClick: () => handleBulkEdit(),
      disabled: (data: EnrichedBin[], selected?: EnrichedBin[]) => selectedBinCodes.length === 0,
      variant: 'primary'
    },
    {
      id: 'refresh',
      label: 'Oppdater',
      icon: RefreshCw,
      onClick: () => refreshData(),
      variant: 'secondary'
    }
  ], [selectedBinCodes.length, handleBulkEdit, handleCreateNew, refreshData]);

  return (
    <div className="flex flex-col h-full overflow-hidden bg-gray-50">
      <div className="flex-1 min-h-0">
        <WarehouseTable
          data={serverBins}
          columns={columns}
          config={{
            showPagination: true,
            showColumnConfig: true,
            showGlobalSearch: false,
            showFilters: true,
            showExport: false,
            showPrint: true,
            showSummary: false,
            defaultPageSize: 50,
            allowColumnReorder: true,
            allowColumnResize: true,
            stickyHeader: true,
            compactMode: true,
            persistenceKey: 'locations-table',
            allowRowSelection: {
              mode: 'multi',
              selectAllMode: 'current-page'
            }
          }}
          serverPagination={{
            enabled: true,
            totalRecords: totalRecords,
            onFetchData: handleServerFetch
          }}
          selectedRows={selectedBinCodes}
          onSelectionChange={handleSelectionChange}
          getRowId={getRowId}
          tableActions={tableActions}
          inlineEditConfig={inlineEditConfig}
          loading={isLoading}
          error={error || undefined}
        />
      </div>

      {/* Bulk Edit Modal */}
      {showBulkEditModal && selectedBinCodes.length > 0 && (() => {
        const selectedWarehouseID = serverBins.find(b => b.binCode === selectedBinCodes[0])?.warehouseID || '';

        return (
          <BulkUpdateModal
            isOpen={showBulkEditModal}
            onClose={() => setShowBulkEditModal(false)}
            selectedBinCodes={selectedBinCodes}
            warehouseID={selectedWarehouseID}
            filterOptions={null}
            onUpdateSuccess={handleUpdateSuccess}
            onDeleteSuccess={handleDeleteSuccess}
            selectedBinData={serverBins.filter(b => selectedBinCodes.includes(b.binCode))}
          />
        );
      })()}

      {/* Create Modal */}
      {showCreateModal && (
        <LocationCreateModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleCreateSuccess}
          masterData={masterData}
        />
      )}

      {/* Print Labels Modal */}
      {showPrintModal && selectedBinCodes.length > 0 && (
        <LabelPrintModal
          isOpen={showPrintModal}
          onClose={() => setShowPrintModal(false)}
          printType="location"
          items={serverBins
            .filter(b => selectedBinCodes.includes(b.binCode))
            .map(b => ({
              id: b.binCode,
              displayName: b.description1 ? `${b.binCode} - ${b.description1}` : b.binCode,
              binCode: b.binCode,
              locationName: b.binCode,
              warehouseId: b.warehouseID,
              zoneName: b.zoneName,
            }))}
        />
      )}
    </div>
  );
};
